// ─── FinalCustomersView — gestão de Clientes Finais (Petrotec Angola) ──────
//
// Listagem + CRUD básico dos FinalCustomers (SONANGOL, TOTAL, ETU…) usados
// na hierarquia FinalCustomer → CustomerSite → SiteEquipment.
//
// Apenas montado quando `activeView === 'finalCustomers'`, e a única forma
// de chegar aqui é pelo item de menu no Header gated por
// `operationOrigin === 'ao'`. Tenants em PT nunca vêem este ecrã.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useFormCardScroll } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import BusinessIcon from '@material-ui/icons/Business';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import BuildIcon from '@material-ui/icons/Build';
import { setSelectedCustomerSite } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';
import { setActiveView, setSelectedFinalCustomer } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, maxWidth: 360,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff', fontSize: '0.78rem' },
  },
  addButton: {
    background: '#3b82f6',
    color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700,
    padding: '6px 16px', fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 10,
    border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 14,
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
  },
  cardInactive: { opacity: 0.55, background: '#f8fafc' },
  cardIcon: {
    width: 40, height: 40, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: '#fee2e2',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: {
    fontSize: '0.92rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    fontSize: '0.7rem', color: '#64748b', marginTop: 4,
    display: 'flex', gap: 12, flexWrap: 'wrap',
  },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
}));

// Schedule default — Seg-Sex 08:00-17:00 (genérico). Cada cliente pode
// personalizar (SONANGALP = 07:30-17:30, TOTAL = 07:00-17:00 + Sáb 7-12,
// confirmado pela Petrotec em 2026-06-21).
const DEFAULT_SCHEDULE = {
  monday:    { start: '08:00', end: '17:00' },
  tuesday:   { start: '08:00', end: '17:00' },
  wednesday: { start: '08:00', end: '17:00' },
  thursday:  { start: '08:00', end: '17:00' },
  friday:    { start: '08:00', end: '17:00' },
  saturday:  null,
  sunday:    null,
};
const DAYS = [
  { key: 'monday',    label: 'Segunda' },
  { key: 'tuesday',   label: 'Terça' },
  { key: 'wednesday', label: 'Quarta' },
  { key: 'thursday',  label: 'Quinta' },
  { key: 'friday',    label: 'Sexta' },
  { key: 'saturday',  label: 'Sábado' },
  { key: 'sunday',    label: 'Domingo' },
];

const EMPTY_FORM = { name: '', nif: '', entity: '', primaveraId: '', workSchedule: DEFAULT_SCHEDULE };

function FinalCustomersView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);          // null = nenhum / 'new' = criar / number = editar
  const formCardRef = useFormCardScroll(editingId);
  // Árvore expansível: { [customerId]: { loading, sites: [{...site, equipments: [...]}], error } }
  // Carregado lazy quando o operador clica para expandir um cliente.
  const [expandedTree, setExpandedTree] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = useCallback(async (customerId) => {
    if (expandedId === customerId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(customerId);
    if (expandedTree[customerId]) return; // cache
    setExpandedTree(prev => ({ ...prev, [customerId]: { loading: true, sites: [], error: null } }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sitesRes, equipsRes] = await Promise.all([
        fetch(`/planner-api/customer-sites?finalCustomerId=${customerId}`, { headers }),
        fetch(`/planner-api/site-equipments?finalCustomerId=${customerId}`, { headers }),
      ]);
      if (!sitesRes.ok) throw new Error(`sites HTTP ${sitesRes.status}`);
      const sites = await sitesRes.json();
      const equipments = equipsRes.ok ? await equipsRes.json() : [];
      // Agrupa equipamentos pelo siteId
      const eqBySite = {};
      for (const eq of equipments) {
        if (!eqBySite[eq.siteId]) eqBySite[eq.siteId] = [];
        eqBySite[eq.siteId].push(eq);
      }
      const sitesWithEq = sites.map(s => ({ ...s, equipments: eqBySite[s.id] || [] }));
      setExpandedTree(prev => ({ ...prev, [customerId]: { loading: false, sites: sitesWithEq, error: null } }));
    } catch (e) {
      setExpandedTree(prev => ({ ...prev, [customerId]: { loading: false, sites: [], error: e.message } }));
    }
  }, [token, expandedId, expandedTree]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);  // {id, name}

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const qs = showInactive ? '?includeInactive=1' : '';
      const r = await fetch(`/planner-api/final-customers${qs}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('FinalCustomers fetch error:', e);
      setError(pt ? 'Não foi possível carregar a lista.' : 'Could not load list.');
    } finally {
      setLoading(false);
    }
    // headers is derived from token; depending on showInactive too is required for re-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function startCreate() {
    setForm(EMPTY_FORM);
    setEditingId('new');
  }

  function startEdit(c) {
    setForm({
      name: c.name || '',
      nif: c.nif || '',
      entity: c.entity || '',
      primaveraId: c.primaveraId || '',
      // Pré-popula com schedule guardado; cai no DEFAULT_SCHEDULE se
      // null (cliente legacy criado antes desta feature). Operador pode
      // ajustar antes de gravar.
      workSchedule: c.workSchedule && Object.keys(c.workSchedule).length > 0
        ? { ...DEFAULT_SCHEDULE, ...c.workSchedule }
        : DEFAULT_SCHEDULE,
    });
    setEditingId(c.id);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function saveForm() {
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError(pt ? 'Nome obrigatório (mínimo 2 caracteres).' : 'Name required (min 2 chars).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        name: form.name.trim(),
        nif: form.nif.trim() || null,
        entity: form.entity.trim() || null,
        primaveraId: form.primaveraId.trim() || null,
        workSchedule: form.workSchedule || null,
      });
      const isNew = editingId === 'new';
      const url = isNew
        ? '/planner-api/final-customers'
        : `/planner-api/final-customers/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    try {
      const r = await fetch(`/planner-api/final-customers/${c.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ isActive: !c.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/final-customers/${deleteConfirm.id}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  }

  const filtered = customers.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [c.name, c.nif, c.entity, c.primaveraId].some(v =>
      v && String(v).toLowerCase().includes(q),
    );
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back to Maintenance'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <BusinessIcon style={{ fontSize: 20, color: '#dc2626' }} />
            {pt ? 'Clientes Finais' : 'Final Customers'}
          </Typography>
          <Typography className={classes.subtitle}>
            {pt ? 'Empresas a quem a Petrotec presta assistência técnica' : 'Companies serviced by Petrotec'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.searchInput}
          size="small" variant="outlined"
          placeholder={pt ? 'Procurar por nome, NIF, entidade…' : 'Search by name, tax id…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
              {pt ? 'Mostrar desactivados' : 'Show inactive'}
            </Typography>
          }
        />
        <Box style={{ flex: 1 }} />
        <Button
          className={classes.addButton}
          startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate}
          disabled={editingId !== null}
        >
          {pt ? 'Novo cliente final' : 'New final customer'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>
            {error}
          </Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <BusinessIcon style={{ fontSize: 18, color: '#dc2626' }} />
              {editingId === 'new'
                ? (pt ? 'Novo cliente final' : 'New final customer')
                : (pt ? 'Editar cliente final' : 'Edit final customer')}
            </Typography>
            <TextField
              label={pt ? 'Nome *' : 'Name *'}
              size="small" variant="outlined"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              autoFocus
              fullWidth
            />
            <Box className={classes.formRow}>
              <TextField
                label="NIF"
                size="small" variant="outlined"
                value={form.nif}
                onChange={e => setForm({ ...form, nif: e.target.value })}
              />
              <TextField
                label={pt ? 'Entidade (Primavera)' : 'Entity (Primavera)'}
                size="small" variant="outlined"
                value={form.entity}
                onChange={e => setForm({ ...form, entity: e.target.value })}
              />
            </Box>
            <TextField
              label={pt ? 'ID Primavera (opcional)' : 'Primavera ID (optional)'}
              size="small" variant="outlined"
              value={form.primaveraId}
              onChange={e => setForm({ ...form, primaveraId: e.target.value })}
              fullWidth
            />

            {/* ─── Horário de trabalho (H.N.) ──────────────────────────
                Define o que conta como Horas Normais para este cliente.
                Tempo fora deste horário (e feriados Angola) entra como
                H.E. no cálculo do BM. */}
            <Box style={{
              marginTop: 8, padding: '14px 16px',
              border: '1px solid #e0f2fe', background: '#f0f9ff', borderRadius: 10,
            }}>
              <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0c4a6e', marginBottom: 4 }}>
                🕒 Horário de trabalho (H.N.)
              </Typography>
              <Typography style={{ fontSize: '0.66rem', color: '#0369a1', marginBottom: 10, lineHeight: 1.4 }}>
                Tempo dentro deste horário cobra como Horas Normais. Fora (incluindo feriados Angola) cobra como Horas Extra. Desliga um dia para o tratar inteiramente como H.E.
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {DAYS.map(d => {
                  const slot = form.workSchedule?.[d.key];
                  const enabled = !!slot;
                  return (
                    <Box key={d.key} style={{
                      display: 'grid', gridTemplateColumns: '34px 84px 90px 90px',
                      alignItems: 'center', gap: 10,
                      padding: '4px 6px',
                      background: enabled ? '#fff' : 'transparent',
                      borderRadius: 6,
                      opacity: enabled ? 1 : 0.55,
                    }}>
                      <Switch size="small" color="primary" checked={enabled}
                        onChange={(e) => {
                          const ws = { ...(form.workSchedule || DEFAULT_SCHEDULE) };
                          ws[d.key] = e.target.checked ? { start: '08:00', end: '17:00' } : null;
                          setForm({ ...form, workSchedule: ws });
                        }} />
                      <Typography style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 600 }}>
                        {d.label}
                      </Typography>
                      <TextField type="time" size="small" variant="outlined"
                        disabled={!enabled}
                        value={slot?.start || ''}
                        onChange={(e) => {
                          const ws = { ...(form.workSchedule || DEFAULT_SCHEDULE) };
                          ws[d.key] = { ...(ws[d.key] || {}), start: e.target.value, end: ws[d.key]?.end || '17:00' };
                          setForm({ ...form, workSchedule: ws });
                        }}
                        inputProps={{ step: 60, style: { fontSize: '0.78rem', padding: '6px 8px' } }} />
                      <TextField type="time" size="small" variant="outlined"
                        disabled={!enabled}
                        value={slot?.end || ''}
                        onChange={(e) => {
                          const ws = { ...(form.workSchedule || DEFAULT_SCHEDULE) };
                          ws[d.key] = { ...(ws[d.key] || {}), end: e.target.value, start: ws[d.key]?.start || '08:00' };
                          setForm({ ...form, workSchedule: ws });
                        }}
                        inputProps={{ step: 60, style: { fontSize: '0.78rem', padding: '6px 8px' } }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box className={classes.formActions}>
              <Button
                onClick={cancelEdit}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}
                disabled={saving}
              >
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={saveForm}
                disabled={saving}
                className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}
              >
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && customers.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>
              {pt ? 'A carregar…' : 'Loading…'}
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            <BusinessIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {search
                ? (pt ? 'Sem resultados para a pesquisa.' : 'No results for search.')
                : (pt ? 'Ainda não há clientes finais.' : 'No final customers yet.')}
            </Typography>
            {!search && (
              <Button
                className={classes.addButton}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}
                disabled={editingId !== null}
              >
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
          </Box>
        ) : (
          filtered.map(c => {
            const isExpanded = expandedId === c.id;
            const tree = expandedTree[c.id];
            return (
              <Box key={c.id} style={{ marginBottom: 8 }}>
                <Box className={`${classes.card} ${!c.isActive ? classes.cardInactive : ''}`}
                  style={{ marginBottom: 0, borderRadius: isExpanded ? '12px 12px 0 0' : 12 }}>
                  <Tooltip title={isExpanded ? (pt ? 'Recolher' : 'Collapse') : (pt ? 'Expandir postos & equipamentos' : 'Expand sites & equipment')}>
                    <IconButton size="small" onClick={() => toggleExpand(c.id)}
                      style={{ marginRight: 4 }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                  <Box className={classes.cardIcon}>
                    <BusinessIcon style={{ fontSize: 20, color: '#dc2626' }} />
                  </Box>
                  <Box className={classes.cardMain}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography className={classes.cardName}>{c.name}</Typography>
                      {!c.isActive && (
                        <Chip label={pt ? 'Desactivado' : 'Inactive'} size="small"
                          style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569' }} />
                      )}
                    </Box>
                    <Box className={classes.cardMeta}>
                      {c.nif && <span>NIF: {c.nif}</span>}
                      {c.entity && <span>{pt ? 'Entidade' : 'Entity'}: {c.entity}</span>}
                      {c.primaveraId && <span>Primavera: {c.primaveraId}</span>}
                    </Box>
                  </Box>
                  <Box className={classes.cardActions}>
                    <Tooltip title={pt ? 'Ver postos (drilldown)' : 'View sites (drilldown)'}>
                      <Button size="small"
                        onClick={() => {
                          dispatch(setSelectedFinalCustomer({ id: c.id, name: c.name }));
                          dispatch(setActiveView('customerSites'));
                        }}
                        endIcon={<ChevronRightIcon style={{ fontSize: 16 }} />}
                        startIcon={<LocalGasStationIcon style={{ fontSize: 14 }} />}
                        style={{
                          textTransform: 'none', fontSize: '0.7rem', fontWeight: 700,
                          color: '#dc2626', background: 'rgba(220,38,38,0.06)',
                          padding: '4px 10px', borderRadius: 8,
                        }}>
                        {pt ? 'Postos' : 'Sites'}{c.siteCount > 0 ? ` (${c.siteCount})` : ''}
                      </Button>
                    </Tooltip>
                    <Tooltip title={c.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                      <IconButton size="small" onClick={() => toggleActive(c)}>
                        {c.isActive
                          ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                          : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={pt ? 'Editar' : 'Edit'}>
                      <IconButton size="small" onClick={() => startEdit(c)} disabled={editingId !== null}>
                        <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                      <IconButton size="small" onClick={() => setDeleteConfirm({ id: c.id, name: c.name })}>
                        <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Painel expansível — árvore Postos → Equipamentos */}
                {isExpanded && (
                  <Box style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '10px 16px 14px 50px',
                  }}>
                    {!tree || tree.loading ? (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: '0.78rem', padding: '8px 0' }}>
                        <CircularProgress size={14} />
                        {pt ? 'A carregar postos e equipamentos…' : 'Loading sites and equipment…'}
                      </Box>
                    ) : tree.error ? (
                      <Typography style={{ fontSize: '0.78rem', color: '#dc2626' }}>
                        {pt ? 'Falha a carregar: ' : 'Failed: '}{tree.error}
                      </Typography>
                    ) : tree.sites.length === 0 ? (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: '0.78rem', padding: '4px 0' }}>
                        <LocalGasStationIcon style={{ fontSize: 16 }} />
                        {pt ? 'Sem postos registados. Adiciona pelo botão "Postos" acima.' : 'No sites yet.'}
                      </Box>
                    ) : (
                      <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {tree.sites.map(site => (
                          <Box key={site.id}>
                            <Box style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 8px', borderRadius: 6,
                              cursor: 'pointer',
                            }}
                              onClick={() => {
                                dispatch(setSelectedFinalCustomer({ id: c.id, name: c.name }));
                                dispatch(setSelectedCustomerSite({ id: site.id, name: site.name }));
                                dispatch(setActiveView('siteEquipments'));
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ color: '#cbd5e1', fontSize: 14 }}>├─</span>
                              <LocalGasStationIcon style={{ fontSize: 16, color: '#0ea5e9' }} />
                              <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', flex: 1 }}>
                                {site.name}
                              </Typography>
                              {site.address && (
                                <Typography style={{ fontSize: '0.66rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                                  📍 {site.address}
                                </Typography>
                              )}
                              <Chip label={`${site.equipments.length} eq.`} size="small"
                                style={{ height: 18, fontSize: '0.6rem', background: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                              <ChevronRightIcon style={{ fontSize: 14, color: '#cbd5e1' }} />
                            </Box>

                            {/* Equipamentos do posto — sub-árvore */}
                            {site.equipments.length > 0 && (
                              <Box style={{ paddingLeft: 38, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {site.equipments.map(eq => (
                                  <Box key={eq.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '3px 4px', fontSize: '0.74rem', color: '#475569',
                                  }}>
                                    <span style={{ color: '#cbd5e1', fontSize: 13 }}>└─</span>
                                    <BuildIcon style={{ fontSize: 13, color: '#f59e0b' }} />
                                    <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                      {eq.petrotecNumber || eq.serialNumber || `Eq-${eq.id}`}
                                    </span>
                                    {eq.equipmentType && (
                                      <span style={{ color: '#64748b' }}>· {eq.equipmentType}</span>
                                    )}
                                    {eq.subtype && (
                                      <Chip label={eq.subtype} size="small"
                                        style={{ height: 16, fontSize: '0.58rem', background: '#fef3c7', color: '#92400e' }} />
                                    )}
                                    {eq.isActive === false && (
                                      <Chip label={pt ? 'Inactivo' : 'Inactive'} size="small"
                                        style={{ height: 16, fontSize: '0.58rem', background: '#fee2e2', color: '#991b1b' }} />
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar cliente final?' : 'Deactivate final customer?'}
        message={deleteConfirm
          ? (pt
            ? `"${deleteConfirm.name}" passa a desactivado. Não é apagado da base de dados — pode ser reactivado em qualquer momento. OTs já criadas mantêm a referência.`
            : `"${deleteConfirm.name}" will be deactivated. Not removed from the database — can be reactivated at any time. Existing OTs keep the reference.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default FinalCustomersView;
